import { supabase } from "./supabase";

const SUPABASE_URL =
  import.meta.env.VITE_SUPABASE_URL || "http://localhost:54321";
const API_URL = `${SUPABASE_URL}/functions/v1`;

export interface User {
  id: string;
  email: string;
  username: string;
  role: "student" | "parent" | "teacher" | "school_admin" | "admin";
  avatar?: string;
  is_premium: boolean;
  parent_id?: string;
  linked_child_ids: string[];
  first_name?: string;
  last_name?: string;
  subscription_tier: "free" | "individual" | "parent" | "teacher";
  subscription_status: "none" | "active" | "cancelled" | "past_due" | "non_renewing";
  school_id?: string;
  grade?: string;
}

export interface School {
  id: string;
  name: string;
  slug: string;
  description?: string;
  logo_url?: string;
  school_code: string;
  address?: string;
  city?: string;
  state?: string;
  country?: string;
  contact_email?: string;
  contact_phone?: string;
  max_teachers: number;
  max_students: number;
  is_active: boolean;
  academic_year?: string;
  settings: Record<string, any>;
  created_at: string;
  counts?: {
    teachers: number;
    students: number;
    classrooms: number;
  };
}

export interface AdminSchoolListItem {
  id: string;
  name: string;
  slug: string;
  is_active: boolean;
  school_code: string;
  max_teachers: number;
  max_students: number;
  created_at: string;
  contact_email?: string;
  city?: string;
  state?: string;
  country?: string;
  teacher_count: number;
  student_count: number;
  classroom_count: number;
  admin_email?: string;
  admin_username?: string;
}

export interface FormSubmission {
  id: string;
  user_id: string | null;
  email: string;
  form_type: "feedback" | "bug_report" | "beta_testing" | "feature_request" | "content_submission" | "partnership_inquiry";
  form_data: Record<string, any>;
  status: "pending" | "in_progress" | "reviewed" | "resolved" | "archived" | "invited" | "accepted";
  admin_notes: string | null;
  processed_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface SchoolMember {
  id: string;
  school_id: string;
  user_id: string;
  role: "school_admin" | "teacher" | "student";
  status: "active" | "inactive" | "removed";
  joined_at: string;
  username?: string;
  email?: string;
  first_name?: string;
  last_name?: string;
  classroom_count?: number;
  classrooms?: string[];
}

export interface AuthResponse {
  access_token: string;
  token_type: string;
  user: User;
}

export interface Technique {
  id: string;
  name: string;
  category:
    | "vedic_math"
    | "russian_math"
    | "universal_tricks"
    | "problem_solving";
  description: string;
  video_url?: string;
  explanation: string; // Legacy plain text explanation
  enhanced_explanation?: object; // New structured JSON explanation (takes precedence if present)
  why_it_works: string;
  examples: { problem: string; solution: string; steps: string }[];
  difficulty: "easy" | "medium" | "hard";
  is_premium: boolean;
  level?: number;
  tracks?: string[];
  prerequisites?: string[];
}

export interface PracticeSession {
  id: string;
  user_id: string;
  technique_id?: string;
  mode: "technique_drill" | "mixed_challenge" | "beat_the_clock" | "tournament";
  tournament_id?: string;
  problems_attempted: number;
  problems_correct: number;
  total_time: number;
  started_at: string;
  completed_at?: string;
  is_personal_best?: boolean;
  previous_best?: number;
  xp_earned?: number;
  xp_awarded?: boolean;
  daily_xp_limit_reached?: boolean;
  metadata?: Record<string, any>;
}

export interface TournamentSessionResponse {
  session: PracticeSession;
  tournament: {
    id: string;
    type: string;
    technique_id?: string;
    difficulty?: string;
    level?: number;
  };
}

export interface TournamentCompletionResponse {
  session: PracticeSession;
  tournament: {
    id: string;
    name?: string;
    status?: string;
    end_time?: string;
  };
  score: {
    tournament_score: number;
    total_tournament_score: number;
    current_rank: number | null;
    accuracy: number;
    problems_correct: number;
    problems_attempted: number;
    time_taken: number;
  };
  leaderboard: Array<{
    user_id: string;
    score: number;
  }>;
}

export type ProblemType = "numeric" | "yes_no" | "text" | "multiple_choice";

export interface Problem {
  id: string;
  technique_id: string;
  difficulty: string;
  question: string;
  answer: string;
  explanation?: string;
  type?: ProblemType;
  options?: string[]; // Answer choices when type is "multiple_choice"
}

export interface TournamentProblem {
  id: string;
  tournament_id: string;
  technique_id: string;
  problem_order: number;
  question: string;
  difficulty: string;
  // Note: 'answer' field is NOT included - it's server-side only!
}

export interface Tournament {
  id: string;
  name: string;
  description: string;
  tournament_type:
    | "weekly_challenge"
    | "monthly_championship"
    | "special_event"
    | "technique_focused";
  technique_id?: string;
  start_time: string;
  end_time: string;
  status: "upcoming" | "active" | "completed" | "cancelled";
  max_participants?: number;
  entry_fee_xp: number;
  prize_pool_xp: number;
  min_rank_tier?: string;
  difficulty?: string;
  created_at: string;
  participant_count?: number;
  is_enrolled?: boolean;
}

export interface TechniqueProgress {
  user_id: string;
  technique_id: string;
  mastery_level: "novice" | "beginner" | "intermediate" | "advanced" | "expert";
  total_problems: number;
  correct_problems: number;
  average_time: number;
  last_practiced: string;
}

export interface DashboardStats {
  overall_accuracy: number;
  average_speed: number;
  current_streak: number;
  total_badges: number;
  techniques_mastered: number;
  recent_sessions: PracticeSession[];
}

export interface Roadmap {
  id: string;
  name: string;
  slug: string;
  description: string;
  objective?: string;
  target_audience?: string;
  icon?: string;
  is_published: boolean;
  display_order: number;
  module_count?: number;
  modules?: Module[];
}

export interface Module {
  id: string;
  roadmap_id: string;
  name: string;
  slug: string;
  description: string;
  module_number: number;
  estimated_duration_minutes?: number;
  icon?: string;
  lessons?: Lesson[];
  progress?: ModuleProgress;
}

export interface Lesson {
  id: string;
  module_id: string;
  technique_id: string;
  technique_name?: string;
  lesson_number: number;
  title: string;
  slug: string;
  description: string;
  instructions: string;
  explanation?: string;
  example_problems: { problem: string; solution: string; steps: string }[];
  practice_config: {
    min_problems: number;
    difficulty: string;
    problem_types: string[];
  };
  completion_criteria: { accuracy_threshold: number; min_problems: number };
  hints?: string[];
  video_url?: string;
  estimated_duration_minutes?: number;
  lesson_type?: "learn" | "forge" | "challenge";
  tournament_id?: string;
  progress?: LessonProgress;
}

export interface LessonProgress {
  id: string;
  user_id: string;
  lesson_id: string;
  status: "locked" | "unlocked" | "in_progress" | "completed";
  problems_attempted: number;
  problems_correct: number;
  current_accuracy: number;
  best_accuracy: number;
  total_time_spent_seconds: number;
  attempts: number;
  first_started_at?: string;
  completed_at?: string;
  last_practiced_at?: string;
}

export interface ModuleProgress {
  total_lessons: number;
  completed_lessons: number;
  unlocked_lessons: number;
  in_progress_lessons: number;
  locked_lessons: number;
  completion_percentage: number;
  technique_practiced: boolean;
  technique_mastery_level?:
    | "novice"
    | "beginner"
    | "intermediate"
    | "advanced"
    | "expert";
}

// ===== TEACHER-RELATED TYPES =====

export interface Classroom {
  id: string;
  teacher_id: string;
  name: string;
  description: string | null;
  grade_level: string | null;
  subject: string;
  class_code: string;
  max_students: number;
  is_active: boolean;
  allow_self_enrollment: boolean;
  school_name: string | null;
  academic_year: string | null;
  created_at: string;
  updated_at: string;
}

export interface ClassroomStudent {
  id: string;
  classroom_id: string;
  student_id: string;
  enrolled_at: string;
  status: "active" | "inactive" | "withdrawn";
  student_info?: {
    id: string;
    username: string;
    email: string;
  };
}

export interface ClassroomInvitation {
  id: string;
  classroom_id: string;
  teacher_id: string;
  student_id: string | null;
  student_email: string | null;
  student_username: string | null;
  status: "pending" | "accepted" | "declined" | "cancelled";
  sent_at: string;
  responded_at: string | null;
  expires_at: string;
  created_at: string;
}

export interface Assignment {
  id: string;
  classroom_id: string;
  teacher_id: string;
  title: string;
  description: string | null;
  instructions: string | null;
  assignment_type:
    | "technique_practice"
    | "lesson_completion"
    | "problem_set"
    | "timed_challenge"
    | "accuracy_challenge";
  content: Record<string, any>;
  grading_criteria: Record<string, any>;
  points_possible: number;
  auto_grade: boolean;
  assigned_date: string;
  due_date: string | null;
  publish_date: string | null;
  allow_late_submission: boolean;
  late_penalty_percent: number;
  is_published: boolean;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface AssignmentWithStats extends Assignment {
  classrooms: {
    id: string;
    name: string;
    grade_level: string | null;
  };
  stats: {
    total_students: number;
    submitted: number;
    graded: number;
    completion_rate: number;
  };
}

export interface AssignmentTemplate {
  id: string;
  teacher_id: string;
  name: string;
  description: string | null;
  assignment_type:
    | "technique_practice"
    | "lesson_completion"
    | "problem_set"
    | "timed_challenge"
    | "accuracy_challenge";
  content: Record<string, any>;
  grading_criteria: Record<string, any>;
  points_possible: number;
  auto_grade: boolean;
  suggested_due_days: number | null;
  allow_late_submission: boolean;
  late_penalty_percent: number;
  is_favorite: boolean;
  usage_count: number;
  last_used_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface BulkImportResult {
  email?: string;
  username?: string;
  status: "success" | "failed";
  error?: string;
}

export interface BulkImportOperation {
  id: string;
  teacher_id: string;
  classroom_id: string;
  total_rows: number;
  successful_count: number;
  failed_count: number;
  results: BulkImportResult[];
  status: "pending" | "processing" | "completed" | "failed";
  started_at: string;
  completed_at: string | null;
  created_at: string;
}

export interface StudentPerformance {
  student_id: string;
  sessions: number;
  problems_solved: number;
  problems_correct: number;
  accuracy: number;
  time_spent_minutes: number;
  last_active: string | null;
  techniques_practiced: number;
  techniques_mastered: number;
  current_streak: number;
  longest_streak: number;
  assignments_completed: number;
  assignments_total: number;
  assignment_avg_score: number;
  technique_breakdown: Array<{
    technique_id: string;
    mastery_level: string;
    accuracy: number;
    total_problems: number;
  }>;
}

export interface ClassAnalytics {
  classroom: Classroom;
  analytics: {
    total_students: number;
    active_students: number;
    class_metrics: {
      avg_accuracy: number;
      avg_problems_solved: number;
      avg_time_spent_minutes: number;
      avg_sessions: number;
      total_techniques_mastered: number;
      avg_current_streak: number;
    };
    top_performers: Array<{
      user_id: string;
      accuracy: number;
      problems_solved: number;
      techniques_mastered: number;
      student_info: {
        id: string;
        username: string;
      };
    }>;
    struggling_students: Array<{
      user_id: string;
      accuracy: number;
      problems_solved: number;
      last_active: string | null;
      days_inactive: number;
      student_info: {
        id: string;
        username: string;
      };
    }>;
  };
  date_range_days: number;
}

export interface ModuleAssignment {
  assignment_id: string;
  classroom_id: string;
  classroom_name: string;
  module_id: string;
  module_name: string;
  module_slug: string;
  roadmap_id: string;
  roadmap_name: string;
  roadmap_slug: string;
  technique_ids: string[];
  practice_days_required: number;
  window_days: number;
  challenges_count_as_practice: boolean;
  progress: {
    status: "not_started" | "in_progress" | "completed" | "missed";
    practice_days_completed: number;
    window_expires_at: string | null;
    days_remaining: number | null;
    started_at: string | null;
  };
}

export interface PracticeOverviewStudent {
  student_id: string;
  username: string;
  first_name?: string;
  last_name?: string;
  modules: {
    assignment_id: string;
    module_id: string;
    module_name: string;
    module_slug: string;
    roadmap_id: string;
    roadmap_name: string;
    roadmap_slug: string;
    practice_days_completed: number;
    practice_days_required: number;
    days_remaining: number;
    status: string;
  }[];
}

export interface PracticeDetailDay {
  date: string;
  practiced: boolean;
  techniques_practiced?: { technique_id: string; name: string; accuracy: number }[];
  source?: string;
  problems_attempted?: number;
}

// Simple in-memory cache for techniques (5-minute TTL)
interface CacheEntry<T> {
  data: T;
  timestamp: number;
}

const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes
const techniquesCache: Map<string, CacheEntry<Technique[]>> = new Map();

class ApiClient {
  setToken(token: string) {
    localStorage.setItem("token", token);
  }

  async getToken(): Promise<string | null> {
    const {
      data: { session },
    } = await supabase.auth.getSession();
    return session?.access_token || null;
  }

  clearToken() {
    localStorage.removeItem("token");
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {},
    timeoutMs: number = 30000,
  ): Promise<T> {
    const token = await this.getToken();
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
      ...(options.headers as Record<string, string>),
    };

    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }

    const fullUrl = `${API_URL}${endpoint}`;

    try {
      // Add configurable timeout (default 30 seconds)
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

      const response = await fetch(fullUrl, {
        ...options,
        headers,
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        const errorText = await response.text();
        let errorMessage = "Request failed";
        try {
          const errorData = JSON.parse(errorText);
          errorMessage = errorData.error || errorData.detail || errorMessage;
        } catch {
          errorMessage = errorText || errorMessage;
        }
        throw new Error(errorMessage);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      if (error instanceof Error && error.name === "AbortError") {
        throw new Error("Request timeout - please check your connection");
      }
      throw error;
    }
  }

  async register(
    email: string,
    username: string,
    password: string,
    role: "student" | "parent" | "teacher" = "student",
  ): Promise<AuthResponse> {
    // Note: register function not yet implemented in Edge Functions
    // Use supabase.auth.signUp() directly in AuthContext instead
    const response = await this.request<AuthResponse>("/register", {
      method: "POST",
      body: JSON.stringify({ email, username, password, role }),
    });
    this.setToken(response.access_token);
    return response;
  }

  async login(email: string, password: string): Promise<AuthResponse> {
    // Note: login function not yet implemented in Edge Functions
    // Use supabase.auth.signInWithPassword() directly in AuthContext instead
    const response = await this.request<AuthResponse>("/login", {
      method: "POST",
      body: JSON.stringify({ email, password }),
    });
    this.setToken(response.access_token);
    return response;
  }

  async getCurrentUser(): Promise<User> {
    return this.request<User>("/user-me");
  }

  async getDashboard(): Promise<DashboardStats> {
    return this.request<DashboardStats>("/user-dashboard");
  }

  async getTechniques(options?: {
    view?: "list";
    category?: string;
    level?: number;
  }): Promise<Technique[]> {
    const params = new URLSearchParams();
    if (options?.view) params.append("view", options.view);
    if (options?.category) params.append("category", options.category);
    if (options?.level !== undefined)
      params.append("level", options.level.toString());
    const query = params.toString() ? `?${params.toString()}` : "";

    // Check cache first
    const cacheKey = query || "__all__";
    const cached = techniquesCache.get(cacheKey);
    if (cached && Date.now() - cached.timestamp < CACHE_TTL_MS) {
      return cached.data;
    }

    // Fetch from server and cache
    const data = await this.request<Technique[]>(`/techniques${query}`);
    techniquesCache.set(cacheKey, { data, timestamp: Date.now() });
    return data;
  }

  // Clear techniques cache (call when user logs out or data may have changed)
  clearTechniquesCache(): void {
    techniquesCache.clear();
  }

  async getTechnique(id: string): Promise<Technique> {
    // Fetch single technique directly by ID (optimized)
    return this.request<Technique>(`/techniques?id=${id}`);
  }

  async startPracticeSession(
    mode: string,
    techniqueId?: string,
    assignmentId?: string,
  ): Promise<PracticeSession> {
    const body: any = { mode };
    if (techniqueId) {
      body.technique_id = techniqueId;
    }
    if (assignmentId) {
      body.assignment_id = assignmentId;
    }
    return this.request<PracticeSession>(`/practice-session-start`, {
      method: "POST",
      body: JSON.stringify(body),
    });
  }

  async generateProblem(
    techniqueId: string,
    difficulty: string = "medium",
    problemType?: string,
  ): Promise<Problem> {
    const params = new URLSearchParams({
      technique_id: techniqueId,
      difficulty,
    });
    if (problemType) {
      params.append("problem_type", problemType);
    }
    return this.request<Problem>(
      `/practice-problem-generate?${params.toString()}`,
    );
  }

  async generateProblems(
    techniqueId: string,
    count: number,
    difficulty: string = "medium",
    problemType?: string,
  ): Promise<Problem[]> {
    const params = new URLSearchParams({
      technique_id: techniqueId,
      difficulty,
      count: count.toString(),
    });
    if (problemType) {
      params.append("problem_type", problemType);
    }
    return this.request<Problem[]>(
      `/practice-problem-generate?${params.toString()}`,
    );
  }

  async submitProblemAttempt(
    sessionId: string,
    problemId: string,
    userAnswer: string,
    isCorrect: boolean,
    timeTaken: number,
  ) {
    return this.request(`/practice-session-submit`, {
      method: "POST",
      body: JSON.stringify({
        session_id: sessionId,
        problem_id: problemId,
        user_answer: userAnswer,
        is_correct: isCorrect,
        time_taken: timeTaken,
      }),
    });
  }

  async completePracticeSession(
    sessionId: string,
    problemsAttempted?: number,
    problemsCorrect?: number,
    totalTime?: number,
  ): Promise<PracticeSession> {
    return this.request<PracticeSession>(`/practice-session-complete`, {
      method: "POST",
      body: JSON.stringify({
        session_id: sessionId,
        problems_attempted: problemsAttempted,
        problems_correct: problemsCorrect,
        total_time: totalTime,
      }),
    });
  }

  // Tournament API methods
  async startTournamentSession(
    tournamentId: string,
  ): Promise<TournamentSessionResponse> {
    return this.request<TournamentSessionResponse>(
      `/tournament-session-start`,
      {
        method: "POST",
        body: JSON.stringify({ tournament_id: tournamentId }),
      },
    );
  }

  async completeTournamentSession(
    sessionId: string,
    problemsAttempted: number,
    problemsCorrect: number,
    totalTime: number,
  ): Promise<TournamentCompletionResponse> {
    return this.request<TournamentCompletionResponse>(
      `/tournament-session-complete`,
      {
        method: "POST",
        body: JSON.stringify({
          session_id: sessionId,
          problems_attempted: problemsAttempted,
          problems_correct: problemsCorrect,
          total_time: totalTime,
        }),
      },
    );
  }

  async getUserProgress(): Promise<TechniqueProgress[]> {
    return this.request<TechniqueProgress[]>("/practice-progress");
  }

  async getUserXP(): Promise<any> {
    return this.request<any>("/user-xp");
  }

  async getChildren(): Promise<User[]> {
    return this.request<User[]>("/user-children");
  }

  async linkChild(childCode: string) {
    return this.request("/user-link-child", {
      method: "POST",
      body: JSON.stringify({ child_code: childCode }),
    });
  }

  async getChildDashboard(childId: string): Promise<DashboardStats> {
    return this.request<DashboardStats>(
      `/user-child-dashboard?child_id=${childId}`,
    );
  }

  async getChildAnalytics(childId: string): Promise<any> {
    return this.request<any>(`/child-analytics?child_id=${childId}`);
  }

  async getMultipleChildAnalytics(
    childIds: string[],
  ): Promise<Map<string, any>> {
    const results = await Promise.allSettled(
      childIds.map((id) => this.getChildAnalytics(id)),
    );

    const map = new Map();
    results.forEach((result, idx) => {
      if (result.status === "fulfilled") {
        map.set(childIds[idx], result.value);
      } else {
        // Store error information for the failed child
        map.set(childIds[idx], {
          error: result.reason?.message || "Failed to load data",
        });
      }
    });
    return map;
  }

  async getGlobalRanking(userId: string): Promise<{
    rank: number;
    total_students: number;
    percentile: number;
    rank_tier: string;
    rank_description: string;
    score: number;
    stats: {
      total_problems_solved: number;
      accuracy: number;
      current_streak: number;
      longest_streak: number;
    };
  }> {
    return this.request(`/global-ranking?user_id=${userId}`);
  }

  // MM Roadmap API methods
  async getRoadmaps(includeProgress = false): Promise<Roadmap[]> {
    const url = includeProgress
      ? "/roadmaps?include_progress=true"
      : "/roadmaps";
    return this.request<Roadmap[]>(url);
  }

  async getRoadmap(roadmapId: string): Promise<Roadmap> {
    return this.request<Roadmap>(`/roadmaps?id=${roadmapId}`);
  }

  async startLesson(
    lessonId: string,
  ): Promise<{ lesson: Lesson; progress: LessonProgress }> {
    return this.request<{ lesson: Lesson; progress: LessonProgress }>(
      "/lesson-practice-start",
      {
        method: "POST",
        body: JSON.stringify({ lesson_id: lessonId }),
      },
    );
  }

  async submitLessonAnswer(
    lessonId: string,
    problemsCorrect: number,
    timeSpentSeconds: number,
    problemsAttempted: number = 1,
  ): Promise<{
    progress: LessonProgress;
    lesson_completed: boolean;
    next_lesson_id: string | null;
    meets_criteria: { min_problems: boolean; accuracy_threshold: boolean };
  }> {
    return this.request("/lesson-practice-submit", {
      method: "POST",
      body: JSON.stringify({
        lesson_id: lessonId,
        problems_attempted: problemsAttempted,
        problems_correct: problemsCorrect,
        time_spent_seconds: timeSpentSeconds,
      }),
    });
  }

  // Tournament Pre-Generated Problems API methods
  async verifyTournamentAnswers(
    tournamentId: string,
    answers: Array<{
      problem_id: string;
      user_answer: string;
      time_taken: number;
    }>,
  ): Promise<{
    results: Array<{
      problem_id: string;
      is_correct: boolean;
      correct_answer: string;
      user_answer: string;
    }>;
    score: {
      correct: number;
      total: number;
    };
  }> {
    return this.request<{
      results: Array<{
        problem_id: string;
        is_correct: boolean;
        correct_answer: string;
        user_answer: string;
      }>;
      score: {
        correct: number;
        total: number;
      };
    }>("/tournament-verify-answer", {
      method: "POST",
      body: JSON.stringify({
        tournament_id: tournamentId,
        answers,
      }),
    });
  }

  async generateTournamentProblems(
    tournamentId: string,
    techniqueIds: string[],
    problemsPerTechnique: number = 10,
    difficulty: string = "medium",
  ): Promise<{
    success: boolean;
    problems_generated: number;
    message: string;
  }> {
    return this.request<{
      success: boolean;
      problems_generated: number;
      message: string;
    }>("/tournament-generate-problems", {
      method: "POST",
      body: JSON.stringify({
        tournament_id: tournamentId,
        technique_ids: techniqueIds,
        problems_per_technique: problemsPerTechnique,
        difficulty: difficulty,
      }),
    });
  }

  async getTournament(tournamentId: string): Promise<Tournament> {
    return this.request<Tournament>(`/tournament-get/${tournamentId}`);
  }

  async joinTournament(
    tournamentId: string,
  ): Promise<{ success: boolean; message: string }> {
    return this.request<{ success: boolean; message: string }>(
      "/tournament-join",
      {
        method: "POST",
        body: JSON.stringify({ tournament_id: tournamentId }),
      },
    );
  }

  async createTournament(data: Record<string, unknown>): Promise<{
    success: boolean;
    tournament?: Tournament;
    template?: Record<string, unknown>;
    problems_generated?: number;
  }> {
    return this.request<{
      success: boolean;
      tournament?: Tournament;
      template?: Record<string, unknown>;
      problems_generated?: number;
    }>("/tournament-create", {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  async checkDailyForgeXP(
    techniqueId: string,
  ): Promise<{ xp_already_earned: boolean; message?: string }> {
    return this.request<{ xp_already_earned: boolean; message?: string }>(
      `/practice-check-daily-xp?techniqueId=${techniqueId}`,
    );
  }

  // ===== TEACHER API METHODS =====

  // Classroom Management
  async getClassrooms(): Promise<Classroom[]> {
    return this.request<Classroom[]>("/teacher-classrooms");
  }

  async createClassroom(data: {
    name: string;
    description?: string;
    grade_level?: string;
    subject?: string;
    max_students?: number;
    school_name?: string;
    academic_year?: string;
    allow_self_enrollment?: boolean;
  }): Promise<{ message: string; classroom: Classroom }> {
    return this.request("/create-classroom", {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  async getClassroomStudents(classroomId: string): Promise<ClassroomStudent[]> {
    return this.request<ClassroomStudent[]>(
      `/classroom-students?classroom_id=${classroomId}`,
    );
  }

  async inviteStudent(data: {
    classroom_id: string;
    student_username?: string;
    student_email?: string;
  }): Promise<{ message: string; invitation: any }> {
    return this.request("/invite-student-to-classroom", {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  async getClassroomInvitations(
    classroomId: string,
  ): Promise<ClassroomInvitation[]> {
    const { data, error } = await supabase
      .from("classroom_invitations")
      .select("*")
      .eq("classroom_id", classroomId)
      .eq("status", "pending") // Only show pending invitations
      .order("created_at", { ascending: false });

    if (error) throw error;
    return data || [];
  }

  async removeStudent(
    classroomId: string,
    studentId: string,
  ): Promise<{ message: string }> {
    return this.request("/remove-student-from-classroom", {
      method: "POST",
      body: JSON.stringify({
        classroom_id: classroomId,
        student_id: studentId,
      }),
    });
  }

  // Analytics
  async getClassAnalytics(
    classroomId: string,
    dateRangeDays: number = 30,
  ): Promise<ClassAnalytics> {
    return this.request<ClassAnalytics>(
      `/class-analytics?classroom_id=${classroomId}&view=overview&date_range=${dateRangeDays}`,
    );
  }

  async getStudentPerformance(
    studentId: string,
    classroomId: string,
    dateRangeDays: number = 30,
  ): Promise<{
    student: User;
    performance: StudentPerformance;
  }> {
    return this.request(
      `/class-analytics?view=student&student_id=${studentId}&classroom_id=${classroomId}&date_range=${dateRangeDays}`,
    );
  }

  async getMultipleStudentPerformance(
    studentIds: string[],
    classroomId: string,
    dateRangeDays: number = 30,
  ): Promise<Map<string, { student: User; performance: StudentPerformance }>> {
    const results = await Promise.allSettled(
      studentIds.map((id) =>
        this.getStudentPerformance(id, classroomId, dateRangeDays),
      ),
    );

    const map = new Map();
    results.forEach((result, idx) => {
      if (result.status === "fulfilled") {
        map.set(studentIds[idx], result.value);
      } else {
        // Store error information for the failed student
        map.set(studentIds[idx], {
          error: result.reason?.message || "Failed to load data",
        });
      }
    });
    return map;
  }

  // Assignments
  async getTeacherAssignments(
    classroomId?: string,
    includeCancelled: boolean = false,
  ): Promise<{ assignments: AssignmentWithStats[] }> {
    const query = classroomId
      ? `?classroom_id=${classroomId}&view=teacher&include_cancelled=${includeCancelled}`
      : `?view=teacher&include_cancelled=${includeCancelled}`;
    return this.request(`/assignment-list${query}`);
  }

  async createAssignment(data: {
    classroom_id: string;
    title: string;
    description?: string;
    instructions?: string;
    assignment_type: string;
    content: Record<string, any>;
    grading_criteria?: Record<string, any>;
    points_possible?: number;
    auto_grade?: boolean;
    due_date?: string;
    publish_date?: string;
    allow_late_submission?: boolean;
    late_penalty_percent?: number;
    is_published?: boolean;
    save_as_template?: boolean;
    template_name?: string;
  }): Promise<{
    assignment: Assignment;
    template?: AssignmentTemplate;
    students_assigned: number;
    message: string;
  }> {
    return this.request("/assignment-create", {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  async updateAssignment(
    assignmentId: string,
    data: {
      title?: string;
      description?: string;
      assignment_type?: string;
      content?: Record<string, any>;
      grading_criteria?: Record<string, any>;
      points_possible?: number;
      due_date?: string;
    },
  ): Promise<{
    message: string;
  }> {
    return this.request("/assignment-update", {
      method: "POST",
      body: JSON.stringify({ assignment_id: assignmentId, ...data }),
    });
  }

  async cancelAssignment(assignmentId: string): Promise<{
    message: string;
    assignment: Assignment;
  }> {
    return this.request("/assignment-cancel", {
      method: "POST",
      body: JSON.stringify({ assignment_id: assignmentId }),
    });
  }

  // Assignment Templates
  async getAssignmentTemplates(): Promise<{
    templates: AssignmentTemplate[];
  }> {
    return this.request("/assignment-template-list", {
      method: "GET",
    });
  }

  async createAssignmentTemplate(data: {
    name: string;
    description?: string;
    assignment_type: string;
    content: Record<string, any>;
    grading_criteria?: Record<string, any>;
    points_possible?: number;
    auto_grade?: boolean;
    suggested_due_days?: number;
    allow_late_submission?: boolean;
    late_penalty_percent?: number;
    is_favorite?: boolean;
    from_assignment_id?: string;
  }): Promise<{
    template: AssignmentTemplate;
    message: string;
  }> {
    return this.request("/assignment-template-create", {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  async createAssignmentFromTemplate(
    templateId: string,
    classroomId: string,
    overrides?: {
      title?: string;
      description?: string;
      instructions?: string;
      due_date?: string;
      publish_date?: string;
      is_published?: boolean;
    },
  ): Promise<{
    assignment: Assignment;
    students_assigned: number;
    message: string;
  }> {
    return this.request("/assignment-create-from-template", {
      method: "POST",
      body: JSON.stringify({
        template_id: templateId,
        classroom_id: classroomId,
        ...overrides,
      }),
    });
  }

  async deleteAssignmentTemplate(templateId: string): Promise<void> {
    const { error } = await supabase
      .from("assignment_templates")
      .delete()
      .eq("id", templateId);

    if (error) throw error;
  }

  async toggleTemplateFavorite(
    templateId: string,
    isFavorite: boolean,
  ): Promise<void> {
    const { error } = await supabase
      .from("assignment_templates")
      .update({ is_favorite: isFavorite })
      .eq("id", templateId);

    if (error) throw error;
  }

  // Bulk Student Management
  async bulkInviteStudents(
    classroomId: string,
    students: Array<{ email?: string; username?: string }>,
  ): Promise<BulkImportOperation> {
    return this.request("/bulk-invite-students", {
      method: "POST",
      body: JSON.stringify({
        classroom_id: classroomId,
        students,
      }),
    });
  }

  async getBulkImportStatus(operationId: string): Promise<{
    operation: BulkImportOperation;
  }> {
    return this.request(`/bulk-import-status?operation_id=${operationId}`, {
      method: "GET",
    });
  }

  async regenerateClassCode(classroomId: string): Promise<{
    message: string;
    classroom: Classroom;
  }> {
    return this.request("/classroom-regenerate-code", {
      method: "POST",
      body: JSON.stringify({ classroom_id: classroomId }),
    });
  }

  // Unified Form Submissions
  async submitForm(
    formType:
      | "feedback"
      | "bug_report"
      | "beta_testing"
      | "feature_request"
      | "content_submission"
      | "partnership_inquiry",
    formData: {
      email: string;
      name?: string;
      // Form-specific fields
      category?: string;
      message?: string;
      device_type?: string;
      title?: string;
      description?: string;
      severity?: string;
      steps_to_reproduce?: string;
      expected_behavior?: string;
      actual_behavior?: string;
      browser?: string;
      os?: string;
      page_url?: string;
      use_case?: string;
      priority?: string;
      content_type?: string;
      grade_level?: string;
      subject?: string;
      content_data?: any;
      organization?: string;
      role?: string;
      organization_type?: string;
      phone?: string;
    },
  ): Promise<{ message: string; submission_id: string }> {
    return this.request("/form-submit", {
      method: "POST",
      body: JSON.stringify({ form_type: formType, ...formData }),
    });
  }

  // ===== STUDENT API METHODS =====

  // Get classrooms the student is enrolled in
  async getStudentClassrooms(): Promise<{ classrooms: any[] }> {
    return this.request("/student-classrooms");
  }

  // Get assignments for the student
  async getStudentAssignments(): Promise<{
    assignments: any[];
    categorized: any;
  }> {
    return this.request("/assignment-list?view=student");
  }

  // Submit assignment work
  async submitAssignment(
    assignmentId: string,
    workData: {
      session_ids: string[];
      problems_attempted: number;
      problems_correct: number;
    },
    finalSubmit: boolean = false,
  ): Promise<{
    message: string;
    submission: any;
    auto_graded: boolean;
  }> {
    return this.request("/assignment-submit", {
      method: "POST",
      body: JSON.stringify({
        assignment_id: assignmentId,
        work_data: workData,
        final_submit: finalSubmit,
      }),
    });
  }

  // Get assignment submission details for a student
  async getAssignmentSubmission(assignmentId: string): Promise<{
    submission: any;
    assignment: Assignment;
  }> {
    return this.request(`/assignment-submission?assignment_id=${assignmentId}`);
  }

  // Get practice sessions for an assignment (to aggregate work data)
  async getAssignmentSessions(
    assignmentId: string,
  ): Promise<PracticeSession[]> {
    const { data, error } = await supabase
      .from("practice_sessions")
      .select("*")
      .eq("assignment_id", assignmentId)
      .order("started_at", { ascending: false });

    if (error) throw error;
    return data || [];
  }

  // Get detailed assignment submissions for teachers (with technique breakdown)
  async getAssignmentSubmissions(assignmentId: string): Promise<{
    assignment: any;
    submissions: Array<{
      student_id: string;
      student_username: string;
      student_email: string;
      submission_id: string;
      status: string;
      score: number | null;
      problems_attempted: number;
      problems_correct: number;
      overall_accuracy: number;
      submitted_at: string | null;
      graded_at: string | null;
      technique_breakdown: Array<{
        technique_id: string;
        technique_name: string;
        problems_attempted: number;
        problems_correct: number;
        accuracy: number;
        meets_requirements: boolean;
      }>;
    }>;
  }> {
    return this.request(
      `/assignment-submissions?assignment_id=${assignmentId}`,
    );
  }

  // ===== ONBOARDING & ASSESSMENT API METHODS =====

  // Get user's onboarding status
  async getOnboarding(): Promise<UserOnboarding | null> {
    const { data, error } = await supabase
      .from("user_onboarding")
      .select("*")
      .maybeSingle();

    if (error) throw error;
    return data;
  }

  // Save onboarding preferences
  async saveOnboarding(data: {
    goal: OnboardingGoal;
    weekly_hours: OnboardingWeeklyHours;
  }): Promise<UserOnboarding> {
    return this.request("/onboarding-save", {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  // Complete onboarding
  async completeOnboarding(): Promise<UserOnboarding> {
    return this.request("/onboarding-complete", {
      method: "POST",
    });
  }

  // Start a diagnostic assessment
  async startAssessment(): Promise<DiagnosticAssessment> {
    return this.request("/assessment-start", {
      method: "POST",
    });
  }

  // Submit assessment answer
  async submitAssessmentAnswer(data: {
    assessment_id: string;
    question_id: string;
    user_answer: string | null;
    time_taken_ms: number;
  }): Promise<{ response: OnboardingAssessmentResponse }> {
    return this.request("/assessment-submit-answer", {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  // Complete and evaluate assessment
  async evaluateAssessment(assessmentId: string): Promise<{
    result: OnboardingAssessmentResult;
    skill_levels: UserSkillLevel[];
  }> {
    return this.request("/assessment-evaluate", {
      method: "POST",
      body: JSON.stringify({ assessment_id: assessmentId }),
    });
  }

  // Get user's assessment result
  async getAssessmentResult(
    assessmentId: string,
  ): Promise<DiagnosticAssessment> {
    const { data, error } = await supabase
      .from("diagnostic_assessments")
      .select("*")
      .eq("id", assessmentId)
      .single();

    if (error) throw error;
    return data;
  }

  // Get user's latest assessment
  async getLatestAssessment(): Promise<DiagnosticAssessment | null> {
    const { data, error } = await supabase
      .from("diagnostic_assessments")
      .select("*")
      .order("started_at", { ascending: false })
      .limit(1)
      .maybeSingle();

    if (error) throw error;
    return data;
  }

  // Generate personalized roadmap based on assessment
  // Uses longer timeout (60s) since AI generation can take time
  async generatePersonalizedRoadmap(
    assessmentId: string,
  ): Promise<PersonalizedRoadmap> {
    return this.request(
      "/roadmap-generate",
      {
        method: "POST",
        body: JSON.stringify({ assessment_id: assessmentId }),
      },
      60000, // 60 second timeout for AI-powered roadmap generation
    );
  }

  // Get user's personalized roadmap
  async getPersonalizedRoadmap(): Promise<PersonalizedRoadmap | null> {
    const { data, error } = await supabase
      .from("personalized_roadmaps")
      .select("*")
      .maybeSingle();

    if (error) throw error;
    return data;
  }

  // Get user's skill levels
  async getUserSkillLevels(): Promise<UserSkillLevel[]> {
    const { data, error } = await supabase
      .from("user_skill_levels")
      .select("*")
      .order("score", { ascending: true });

    if (error) throw error;
    return data || [];
  }

  // Generate hints for a problem
  async generateHints(data: {
    problem: string;
    technique_id: string;
    current_step?: number;
  }): Promise<{ hints: ProblemHint[] }> {
    return this.request("/generate-hints", {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  // Get a single module by ID with lessons and progress
  async getModuleById(moduleId: string): Promise<Module | null> {
    // Fetch module with roadmap info
    const { data: module, error: moduleError } = await supabase
      .from("modules")
      .select(
        `
        *,
        roadmaps!inner(id, name, slug)
      `,
      )
      .eq("id", moduleId)
      .single();

    if (moduleError || !module) {
      console.error("Failed to fetch module:", moduleError);
      return null;
    }

    // Fetch lessons for this module
    const { data: lessons, error: lessonsError } = await supabase
      .from("lessons")
      .select("*")
      .eq("module_id", moduleId)
      .order("lesson_number", { ascending: true });

    if (lessonsError) {
      console.error("Failed to fetch lessons:", lessonsError);
      return null;
    }

    // Fetch lesson progress for the current user
    const { data: progressData, error: progressError } = await supabase
      .from("lesson_progress")
      .select("*")
      .in("lesson_id", lessons?.map((l) => l.id) || []);

    if (progressError) {
      console.error("Failed to fetch progress:", progressError);
    }

    // Merge progress into lessons
    const lessonsWithProgress = lessons?.map((lesson) => ({
      ...lesson,
      progress: progressData?.find((p) => p.lesson_id === lesson.id) || null,
    }));

    return {
      ...module,
      roadmap_id: module.roadmaps.id,
      lessons: lessonsWithProgress || [],
    } as Module;
  }

  // ===== BILLING / SUBSCRIPTION =====

  async createCheckoutSession(planId: string): Promise<{ url: string }> {
    return this.request<{ url: string }>("/create-checkout-session", {
      method: "POST",
      body: JSON.stringify({ plan_id: planId }),
    });
  }

  async createPortalSession(): Promise<{ url: string }> {
    return this.request<{ url: string }>("/create-portal-session", {
      method: "POST",
    });
  }
  // ===== CERTIFICATION API METHODS =====

  async getCertificationExams(type?: string): Promise<CertificationExamWithStatus[]> {
    const params = type ? `?type=${type}` : "";
    const data = await this.request<{ exams: CertificationExamWithStatus[] }>(
      `/certification-exams-list${params}`,
    );
    return data.exams;
  }

  async startCertificationExam(examId: string): Promise<CertificationExamAttemptStart> {
    return this.request<CertificationExamAttemptStart>(
      `/certification-exam-start`,
      {
        method: "POST",
        body: JSON.stringify({ exam_id: examId }),
      },
    );
  }

  async submitCertificationExam(
    attemptId: string,
    answers: CertificationAnswer[],
    totalTimeSeconds: number,
  ): Promise<CertificationExamResult> {
    return this.request<CertificationExamResult>(
      `/certification-exam-submit`,
      {
        method: "POST",
        body: JSON.stringify({
          attempt_id: attemptId,
          answers,
          total_time_seconds: totalTimeSeconds,
        }),
      },
    );
  }

  async getUserCertificates(type?: string): Promise<{
    certificates: Certificate[];
    summary: CertificateSummary;
  }> {
    const params = type ? `?type=${type}` : "";
    return this.request<{
      certificates: Certificate[];
      summary: CertificateSummary;
    }>(`/user-certificates${params}`);
  }

  async verifyCertificate(
    certificateNumber: string,
  ): Promise<CertificateVerification> {
    return this.request<CertificateVerification>(
      `/certification-verify?certificate_number=${encodeURIComponent(certificateNumber)}`,
    );
  }

  // ===== MODULE ASSIGNMENT & PRACTICE TRACKING =====

  // School student: get assigned modules
  async getAssignedModules(): Promise<{ modules: ModuleAssignment[] }> {
    return this.request("/student-assigned-modules");
  }

  // School student: log practice after session
  async logPractice(data: {
    technique_id: string;
    source: "training_hall" | "classroom_challenge";
    session_id?: string;
    problems_attempted: number;
    accuracy: number;
  }): Promise<any> {
    return this.request("/student-log-practice", {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  // Teacher: assign modules to classroom
  async assignModulesToClassroom(data: {
    classroom_id: string;
    modules: {
      module_id: string;
      roadmap_id: string;
      technique_ids: string[];
      practice_days_required?: number;
      window_days?: number;
      challenges_count_as_practice?: boolean;
    }[];
  }): Promise<any> {
    return this.request("/classroom-assign-module", {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  // Teacher: remove/pause module assignment
  async removeModuleAssignment(assignment_id: string, action: "pause" | "remove"): Promise<any> {
    return this.request("/classroom-remove-module", {
      method: "POST",
      body: JSON.stringify({ assignment_id, action }),
    });
  }

  // Teacher: update module assignment requirements
  async updateModuleAssignment(data: {
    assignment_id: string;
    student_id?: string;
    updates: Record<string, any>;
  }): Promise<any> {
    return this.request("/classroom-update-module-assignment", {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  // Teacher: get practice overview for classroom
  async getClassroomPracticeOverview(
    classroom_id: string,
    assignment_id?: string,
  ): Promise<{ students: PracticeOverviewStudent[] }> {
    const params = new URLSearchParams({ classroom_id });
    if (assignment_id) params.set("assignment_id", assignment_id);
    return this.request(`/classroom-student-practice-overview?${params}`);
  }

  // Teacher: get practice detail for a student
  async getStudentPracticeDetail(
    student_id: string,
    classroom_id: string,
    assignment_id?: string,
  ): Promise<{ student_id: string; assignments: { assignment_id: string; module_id: string; practice_days: PracticeDetailDay[] }[] }> {
    const params = new URLSearchParams({ student_id, classroom_id });
    if (assignment_id) params.set("assignment_id", assignment_id);
    return this.request(`/classroom-student-practice-detail?${params}`);
  }

  // ===== SCHOOL API METHODS =====

  async getSchoolBySlug(slug: string): Promise<{ id: string; name: string; slug: string; logo_url: string | null; is_active: boolean }> {
    return this.request(`/school-details?slug=${encodeURIComponent(slug)}`);
  }

  async getSchoolDetails(schoolId: string): Promise<School> {
    return this.request<School>(`/school-details?school_id=${schoolId}`);
  }

  async updateSchool(data: { school_id: string } & Partial<School>): Promise<{ message: string; school: School }> {
    return this.request("/update-school", { method: "POST", body: JSON.stringify(data) });
  }

  async getSchoolTeachers(schoolId: string): Promise<{ teachers: SchoolMember[] }> {
    return this.request(`/school-teachers?school_id=${schoolId}`);
  }

  async getSchoolStudents(schoolId: string, opts?: { page?: number; limit?: number; search?: string; classroom_id?: string }): Promise<{ students: SchoolMember[]; total: number; page: number; limit: number }> {
    const params = new URLSearchParams({ school_id: schoolId });
    if (opts?.page) params.set("page", String(opts.page));
    if (opts?.limit) params.set("limit", String(opts.limit));
    if (opts?.search) params.set("search", opts.search);
    if (opts?.classroom_id) params.set("classroom_id", opts.classroom_id);
    return this.request(`/school-students?${params}`);
  }

  async getSchoolClassrooms(schoolId: string): Promise<{ classrooms: any[] }> {
    return this.request(`/school-classrooms?school_id=${schoolId}`);
  }

  async getSchoolAnalytics(schoolId: string, dateRange: number = 30): Promise<any> {
    return this.request(`/school-analytics?school_id=${schoolId}&date_range=${dateRange}`);
  }

  async addTeacherToSchool(schoolId: string, teacherEmail: string): Promise<{ message: string; teacher: any }> {
    return this.request("/school-teachers", { method: "POST", body: JSON.stringify({ action: "add", school_id: schoolId, teacher_email: teacherEmail }) });
  }

  async removeSchoolMember(schoolId: string, memberUserId: string): Promise<{ message: string }> {
    return this.request("/school-students", { method: "POST", body: JSON.stringify({ action: "remove_member", school_id: schoolId, member_user_id: memberUserId }) });
  }

  async createSchoolStudent(data: { school_id: string; user_id_str: string; password: string; first_name?: string; last_name?: string; classroom_id?: string; grade?: string }): Promise<{ message: string; student: any }> {
    return this.request("/school-students", { method: "POST", body: JSON.stringify({ action: "create", ...data }) });
  }

  async bulkCreateSchoolStudents(data: { school_id: string; students: Array<{ user_id_str: string; password: string; first_name?: string; last_name?: string }>; classroom_id?: string }): Promise<{ total: number; successful: number; failed: number; results: any[] }> {
    return this.request("/school-students", { method: "POST", body: JSON.stringify({ action: "bulk_create", ...data }) });
  }

  async resetSchoolStudentPassword(schoolId: string, studentId: string, newPassword: string): Promise<{ message: string }> {
    return this.request("/school-students", { method: "POST", body: JSON.stringify({ action: "reset_password", school_id: schoolId, student_id: studentId, new_password: newPassword }) });
  }

  async regenerateSchoolCode(schoolId: string): Promise<{ message: string; school_code: string; school: School }> {
    return this.request("/regenerate-school-code", { method: "POST", body: JSON.stringify({ school_id: schoolId }) });
  }

  async createSchoolTeacher(data: { school_id: string; user_id_str: string; password: string; first_name?: string; last_name?: string }): Promise<{ message: string; teacher: any }> {
    return this.request("/school-teachers", { method: "POST", body: JSON.stringify({ action: "create", ...data }) });
  }

  async bulkCreateSchoolTeachers(data: { school_id: string; teachers: Array<{ user_id_str: string; password: string; first_name?: string; last_name?: string }> }): Promise<{ total: number; successful: number; failed: number; results: any[] }> {
    return this.request("/school-teachers", { method: "POST", body: JSON.stringify({ action: "bulk_create", ...data }) });
  }

  async createSchoolClassroom(data: { school_id: string; name: string; teacher_id: string; grade_level?: string; description?: string; max_students?: number; academic_year?: string }): Promise<{ message: string; classroom: any }> {
    return this.request("/school-classrooms", { method: "POST", body: JSON.stringify({ action: "create", ...data }) });
  }

  async updateSchoolClassroom(data: { school_id: string; classroom_id: string; name?: string; teacher_id?: string; grade_level?: string; description?: string; max_students?: number }): Promise<{ message: string; classroom: any }> {
    return this.request("/school-classrooms", { method: "POST", body: JSON.stringify({ action: "update", ...data }) });
  }

  async deleteSchoolClassroom(schoolId: string, classroomId: string): Promise<{ message: string }> {
    return this.request("/school-classrooms", { method: "POST", body: JSON.stringify({ action: "delete", school_id: schoolId, classroom_id: classroomId }) });
  }

  async assignStudentsToClassroom(data: { school_id: string; classroom_id: string; add_student_ids?: string[]; remove_student_ids?: string[] }): Promise<{ message: string; added: number; removed: number; errors?: string[] }> {
    return this.request("/school-classrooms", { method: "POST", body: JSON.stringify({ action: "assign_students", ...data }) });
  }

  async updateSchoolStudent(data: { school_id: string; student_id: string; first_name?: string; last_name?: string; grade?: string }): Promise<{ message: string }> {
    return this.request("/school-students", { method: "POST", body: JSON.stringify({ action: "update", ...data }) });
  }

  async toggleSchoolMemberStatus(data: { school_id: string; user_id: string; status: "active" | "inactive" }): Promise<{ message: string; status: string }> {
    return this.request("/school-students", { method: "POST", body: JSON.stringify({ action: "toggle_status", ...data }) });
  }

  // ===== PLATFORM ADMIN API METHODS =====

  async adminListSchools(search?: string): Promise<{ schools: AdminSchoolListItem[] }> {
    const params = search ? `?search=${encodeURIComponent(search)}` : "";
    return this.request(`/admin-schools${params}`);
  }

  async adminCreateSchool(data: {
    name: string;
    slug: string;
    description?: string;
    address?: string;
    city?: string;
    state?: string;
    country?: string;
    contact_email?: string;
    contact_phone?: string;
    max_teachers?: number;
    max_students?: number;
    academic_year?: string;
    admin_email?: string;
  }): Promise<{ message: string; school: School }> {
    return this.request("/admin-schools", { method: "POST", body: JSON.stringify(data) });
  }

  async adminUpdateSchool(data: {
    school_id: string;
    name?: string;
    description?: string;
    address?: string;
    city?: string;
    state?: string;
    country?: string;
    contact_email?: string;
    contact_phone?: string;
    max_teachers?: number;
    max_students?: number;
    academic_year?: string;
    is_active?: boolean;
    admin_email?: string;
  }): Promise<{ message: string; school: School }> {
    return this.request("/admin-schools", { method: "POST", body: JSON.stringify(data) });
  }

  async adminListFeedback(filters?: {
    form_type?: string;
    status?: string;
    search?: string;
  }): Promise<{ submissions: FormSubmission[] }> {
    const params = new URLSearchParams();
    if (filters?.form_type) params.set("form_type", filters.form_type);
    if (filters?.status) params.set("status", filters.status);
    if (filters?.search) params.set("search", filters.search);
    const qs = params.toString();
    return this.request(`/admin-feedback${qs ? `?${qs}` : ""}`);
  }

  async adminUpdateFeedback(data: {
    submission_id: string;
    status?: string;
    admin_notes?: string;
  }): Promise<{ message: string; submission: FormSubmission }> {
    return this.request("/admin-feedback", { method: "POST", body: JSON.stringify(data) });
  }
}

// ===== CERTIFICATION TYPES =====

export interface CertificationExamWithStatus {
  id: string;
  name: string;
  description: string;
  exam_type: "technique" | "category" | "platform";
  technique_id: string | null;
  category: string | null;
  certification_level: number | null;
  problem_count: number;
  time_limit_seconds: number;
  passing_accuracy: number;
  cooldown_hours: number;
  min_user_level: number;
  min_mastery_level: string | null;
  required_certificate_count: number;
  is_certified: boolean;
  certificate: {
    certificate_number: string;
    issued_at: string;
  } | null;
  total_attempts: number;
  best_accuracy: number | null;
  has_in_progress: boolean;
  eligible: boolean;
  eligibility_reason: string | null;
}

export interface CertificationExamAttemptStart {
  attempt_id: string;
  exam: {
    id: string;
    name: string;
    description: string;
    exam_type: string;
    problem_count: number;
    time_limit_seconds: number;
    passing_accuracy: number;
  };
  problems: Array<{
    id: string;
    technique_id: string;
    difficulty: string;
    order: number;
  }>;
  expires_at: string;
  started_at: string;
}

export interface CertificationAnswer {
  problem_id: string;
  answer: string;
  correct_answer: string;
  time_seconds: number;
}

export interface CertificationExamResult {
  attempt_id: string;
  passed: boolean;
  accuracy: number;
  problems_attempted: number;
  problems_correct: number;
  total_time_seconds: number;
  passing_accuracy: number;
  certificate: {
    id: string;
    certificate_number: string;
    title: string;
    description: string;
    issued_at: string;
    verification_hash: string;
  } | null;
  xp_earned: number;
}

export interface Certificate {
  id: string;
  certificate_type: "technique" | "category" | "platform";
  certificate_level: number | null;
  title: string;
  description: string;
  certificate_number: string;
  verification_hash: string;
  accuracy: number;
  problems_correct: number;
  problems_attempted: number;
  time_taken_seconds: number;
  issued_at: string;
  expires_at: string | null;
  exam_name: string | null;
  exam_type: string;
  category: string | null;
  technique_name: string | null;
}

export interface CertificateSummary {
  total: number;
  technique_count: number;
  category_count: number;
  platform_count: number;
  highest_platform_level: number | null;
}

export interface CertificateVerification {
  valid: boolean;
  certificate?: {
    certificate_number: string;
    title: string;
    description: string;
    certificate_type: string;
    certificate_level: number | null;
    holder_name: string;
    accuracy: number;
    problems_correct: number;
    problems_attempted: number;
    time_taken_seconds: number;
    issued_at: string;
    expires_at: string | null;
    status: "valid" | "expired" | "revoked";
  };
  error?: string;
}

// ===== ONBOARDING & ASSESSMENT TYPES =====

export type OnboardingGoal =
  | "competition-prep"
  | "everyday-math"
  | "vedic-mastery"
  | "exam-prep";
export type OnboardingWeeklyHours = 1 | 2 | 3 | 5;
export type OnboardingSkillLevel =
  | "novice"
  | "developing"
  | "proficient"
  | "advanced";

export interface UserOnboarding {
  id: string;
  user_id: string;
  goal: OnboardingGoal | null;
  weekly_hours: OnboardingWeeklyHours | null;
  completed_at: string | null;
  created_at: string;
}

export interface OnboardingAssessmentQuestion {
  id: string;
  type: "solve" | "identify-technique" | "estimate";
  problem: string;
  correct_answer: string;
  options: string[];
  time_limit_seconds: number;
  skills_tested: string[];
  difficulty: "easy" | "medium" | "hard";
}

export interface OnboardingAssessmentResponse {
  question_id: string;
  user_answer: string | null;
  is_correct: boolean;
  time_taken_ms: number;
}

export interface OnboardingSkillScore {
  skill_id: string;
  skill_name: string;
  level: OnboardingSkillLevel;
  score: number;
  correct: number;
  total: number;
}

export interface OnboardingAssessmentResult {
  total_questions: number;
  correct_count: number;
  accuracy_percent: number;
  avg_time_ms: number;
  skill_scores: OnboardingSkillScore[];
}

export interface DiagnosticAssessment {
  id: string;
  user_id: string;
  questions: OnboardingAssessmentQuestion[];
  responses: OnboardingAssessmentResponse[];
  result: OnboardingAssessmentResult | null;
  started_at: string;
  completed_at: string | null;
}

export interface UserSkillLevel {
  id: string;
  user_id: string;
  skill_id: string;
  level: OnboardingSkillLevel;
  score: number;
  assessment_id: string | null;
  updated_at: string;
}

export type PersonalizedModuleStatus =
  | "locked"
  | "available"
  | "in_progress"
  | "completed";

export interface PersonalizedRoadmapModule {
  module_id: string;
  module_name: string;
  roadmap_name: string;
  first_lesson_id: string | null;
  status: PersonalizedModuleStatus;
}

export interface PersonalizedRoadmapWeek {
  week: number;
  theme: string;
  modules: PersonalizedRoadmapModule[];
}

export interface PersonalizedRoadmap {
  id: string;
  user_id: string;
  assessment_id: string | null;
  weeks: PersonalizedRoadmapWeek[];
  total_weeks: number;
  generated_at: string;
}

export interface ProblemHint {
  step: number;
  hint: string;
}

export const api = new ApiClient();
